import { useState, useEffect, useRef, } from 'react'
import loadable from '@loadable/component'
import { Helmet } from "react-helmet-async"
import { arrayMoveImmutable } from 'array-move'

import type {
    TPossibleScreens,
    TUpload,
    TUploadedFile,
    TUploadingError,
    TErrorText,
    TPage,
    // TSignType,
    // TSign,
    TSignPlacerType,
    TSignPlacer,
    TRect,
} from "../types"

import styled from "styled-components"

import {
    saveThumb,
    loadThumb,
    textSign,
} from '../helpers'

import { baseURL, development, isSnap } from '../constants'

import { statOnLoad } from '../Stat'

import Wrapper from "../elements/Wrapper"
import DefaultContent, { Content, SignContent } from "../elements/Content"

import dzIcon from '../../assets/upload-icon.svg'

import {
    Buttons,

    PagePreviwButtons,
    PagePreviwButton,
} from "../elements/Buttons"

import { convert, localUpload, dspManualFormsUpload, dspDocInfo, dspLancerStat, } from "../Services"



import DropZone from "../screens/DropZone"
import { SignH1 } from "../screens/Sign"

const ErrorBlock = loadable(() => import('../elements/Errors'))
const PagesPreview = loadable(() => import('../screens/PagesPreview'))
const Preloader = loadable(() => import("../screens/Preloader"))
const Download = loadable(() => import("../screens/Download"))
const Error = loadable(() => import("../screens/Error"))
const SignPlacer = loadable(() => import("../elements/SignPlacer"))


const InputDocId = styled.input`
    margin-bottom: 16px;
    padding: 12px 0;

    border: 1px solid #c4390b;

    text-align: center;

    outline: none;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type=number] {
        -moz-appearance: textfield;
    }
`

const uploadedFilesT: TUploadedFile[] = []
const uploadingErrors: TUploadingError[] = []
let downloadLink: string
let defaultPagesLength: number

const DSPSignPlacer = () => {
    const [currentScreen, switchScreen] = useState<TPossibleScreens>('filespreview')
    const [progress, setProgress] = useState(0)
    const [uploadedFiles, setUploadedFiles] = useState<TUploadedFile[]>(uploadedFilesT)
    const [countUploadingErrors, setCountUploadingErrors] = useState(uploadingErrors.length)
    const [errorText, setErrorText] = useState<TErrorText>({})
    const [showErrorBlock, setShowErrorBlock] = useState(true)
    const [pages, setPages] = useState<TPage[]>([])
    const [disableButton, setDisableButton] = useState(true)

    const [prep, setPrep] = useState('Uploading File')

    const [rects, setRects] = useState<TSignPlacer[]>([])
    const [editPage, setEditPage] = useState<number | null>(null)

    const [autoRects, setAutoRects] = useState<TRect[]>([])

    const [docId, setDocId] = useState<number>(0)
    const [editDocId, setEditDocId] = useState<number>(0)

    const [signBeacons, setSignBeacons] = useState<{
        height: number,
        width: number,
        x: number,
        y: number,
    }[][]>([])

    const [uid, setUid] = useState<number>(0)
    const [tid, setTid] = useState<number>(0)
    const [paramReady, setParamReady] = useState<boolean>(false)
    
    const [bufferShape, setBufferShape] = useState<TSignPlacer | null>(null)

    useEffect(() => {
        setBufferShape(null)
    }, [docId])

    useEffect(() => {
        if (docId > 0 && currentScreen === 'filespreview') {
            setEditDocId(docId);
        }
    }, [currentScreen])

    const upload: TUpload = {

        action: "signPlacer_dsp",
        allowExtantions: ['pdf'],
        convertTo: "pdf",
        maxSize: 25, // MB
        multiselect: false,

        setDownloadLink: (link: string) => downloadLink = link,
        getDownloadLink: () => downloadLink,
        setProgress: (progress) => setProgress(progress),
        getProgress: () => progress,
        setScreen: (screen) => {
            if (window.scrollY > 220) window.scrollTo({ top: 0, behavior: "smooth" })
            if (screen === 'uploading' || screen === 'converting' || screen === 'preparing') setProgress(0)
            return switchScreen(screen)
        },
        getScreen: () => currentScreen,
        addFiles: (uploadedFile) => {
            return setUploadedFiles([...uploadedFile])
        },
        delFile: (uploadedFile) => {
            return setUploadedFiles(uploadedFiles.filter((element) => element !== uploadedFile))
        },
        clearFiles: () => setUploadedFiles([]),
        getFiles: () => uploadedFiles,
        moveFiles: (oldIndex, newIndex) => {
            return setUploadedFiles(arrayMoveImmutable(uploadedFiles, oldIndex, newIndex))
        },
        addError: (uploadingError) => {
            uploadingErrors.push(uploadingError)
            setShowErrorBlock(true)
            return setCountUploadingErrors(countUploadingErrors + 1)
        },
        delError: (uploadingError) => {
            const idx = uploadingErrors.indexOf(uploadingError)
            if (idx !== -1) {
                uploadingErrors.splice(idx, 1)
                return setCountUploadingErrors(countUploadingErrors - 1)
            }
        },
        clearErrors: () => {
            uploadingErrors.splice(0, uploadingErrors.length)
            return setCountUploadingErrors(0)
        },
        hideErrors: () => setShowErrorBlock(false),
        getErrors: () => uploadingErrors,
        setErrorText: setErrorText,

        getPages: () => pages,
        setPages: (newPages) => {
            setRects([])
            return setPages(newPages)
        },
        setDefaultPages: (pages) => {
            defaultPagesLength = pages.length
            return saveThumb(pages)
        },
    }

    useEffect(() => {
        const token = location.hash.replace('#', '')
        if (token) {
            token.split('&').forEach(param => {
                const par = param.split('=');
                if (
                    String(Number(par[1])) === par[1]
                    && Number.isInteger(Number(par[1]))
                    && par.length === 2
                ) {
                    switch (par[0].toLowerCase()) {
                        case 'id':
                            setDocId(Number(par[1]))
                            break;
                        case 'uid':
                            setUid(Number(par[1]))
                            break;
                        case 'tid':
                            setTid(Number(par[1]))
                            break;
                        default:
                            break;
                    }
                }
            })
            
            setParamReady(true)
        }
    },[])

    useEffect(() => {
        if (docId > 0) {
            dspDocInfo(upload, docId, setSignBeacons, setAutoRects)
            setPrep('Prepairing File')
        }
    }, [paramReady])

    return (
        <Wrapper>
            <Helmet>
                <title>Sign Placer DSP | Templateroller</title>
                <meta name="robots" content="noindex" />
                <meta property="og:image" content={`${baseURL}/tools/assets/og-signPlacer.png`} />
            </Helmet>

            {   // Начальный экран
                currentScreen === 'filespreview' &&
                (pages.length === 0) &&
                <h1>Sign Placer DSP</h1>
            }

            {   // Экран превью страниц
                currentScreen === 'filespreview' &&
                pages.length > 0 &&
                <SignH1>Choose page to place sign</SignH1>
            }

            {
                // Экран завершения
                currentScreen === 'link' &&
                <SignH1>Well done</SignH1>
            }

            {
                currentScreen === 'filespreview' &&
                (pages.length === 0) &&
                <DefaultContent>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }
                    <div
                        className="dz-wrapper dz-content"
                        style={{
                            background: '#fff',
                        }}
                    >
                        <img
                            width="66"
                            height="48"
                            className="dz-title-icon"
                            src={dzIcon}
                            alt="Upload Icon"
                        />
                        <h2 className="dz-title">Sign Placer DSP</h2>
                        <p className="dz-title-description">DSP only</p>
                        {
                            uid === 0 && tid === 0
                            && <>
                                <InputDocId
                                    type='number'
                                    placeholder='Document ID'
                                    onChange={(e) => {
                                        setDocId(Number(e.currentTarget?.value) || 0)
                                    }}
                                    onKeyPress={e => {
                                        if (e.key === 'Enter' && docId > 0) {
                                            dspDocInfo(upload, docId, setSignBeacons, setAutoRects)
                                            setPrep('Prepairing File')
                                        }
                                    }}
                                />
                                <button
                                    className="dz-browse-files"
                                    onClick={() => {
                                        if (docId > 0) {
                                            dspDocInfo(upload, docId, setSignBeacons, setAutoRects)
                                            setPrep('Prepairing File')
                                        }
                                    }}
                                >Get Doc</button>
                            </>
                        }
                    </div>
                </DefaultContent>

            }

            {
                currentScreen === 'filespreview' &&
                pages.length > 0 &&
                <Content>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <PagesPreview
                        upload={upload}
                        sortable={false}
                        setEditPage={setEditPage}
                        signs={rects}
                        beacons={signBeacons}
                        autoRects={autoRects}
                    />

                    <SignPlacer
                        rects={rects}
                        setRects={setRects}
                        editPage={editPage}
                        setEditPage={setEditPage}
                        pages={pages}
                        signBeacons={signBeacons}
                        autoRects={autoRects}
                        bufferShape={bufferShape}
                        setBufferShape={setBufferShape}
                    />

                    <Buttons>
                        <PagePreviwButtons>
                            {
                                uid === 0 && tid === 0
                                && <PagePreviwButton
                                    primary={false}
                                    onClick={() => {
                                        if (rects.length === 0) {
                                            if (confirm('Do you want to upload again?')) {
                                                setUploadedFiles([])
                                                return setPages([])
                                            }
                                        }
                                        else {
                                            if (confirm('Do you want to clear all rects?')) {
                                                return setRects([])
                                            }
                                        }
                                    }}
                                >{`${rects.length === 0 ? 'Reset' : 'Undo'}`}</PagePreviwButton>
                            }
                            {
                                (uid > 0 || tid > 0)
                                && <PagePreviwButton
                                    disabled={!rects.length}
                                    primary={false}
                                    onClick={() => {
                                        if (rects.length) {
                                            if (confirm('Do you want to clear all rects?')) {
                                                return setRects([])
                                            }
                                        }
                                    }}
                                >{`Clear All`}</PagePreviwButton>
                            }
                            
                            <PagePreviwButton
                                primary={true}
                                onClick={() => {
                                    if (docId > 0) {
                                        if (uid > 0 || tid > 0) {
                                            dspLancerStat(docId, uid, tid, 'signplacer')
                                        }
                                        dspManualFormsUpload(upload, docId, rects, pages)
                                    }
                                }}
                            >Apply</PagePreviwButton>
                        </PagePreviwButtons>
                    </Buttons>
                </Content>
            }

            {
                currentScreen === 'uploading' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Uploading File"
                        description="Don’t close this tab, your files are being uploaded. This shouldn’t take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'preparing' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Preparing Pages"
                        description="Don’t close this tab, your files are being prepared. This won't take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'converting' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Processing Document"
                        description="Don’t close this tab, your files are being processed. This won't take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'link' &&
                <DefaultContent>
                        <div
                            className="dz-wrapper dz-content"
                            style={{
                                background: '#fff',
                            }}
                        >
                            <a
                                className="dz-browse-files"
                                style={{
                                    marginBottom: '16px',
                                    textDecoration: 'none',
                                }}
                                target="_blank"
                                href={`https://${development ? 'testnew' : 'www'}.templateroller.com/edit/${editDocId}`}
                            >View in Edit</a>
                            <button
                                className="dz-browse-files"
                                style={{
                                    marginBottom: '16px',
                                }}
                                onClick={() => {
                                    upload.setScreen('filespreview')
                                }}
                            >Back to Placer</button>
                            {
                                uid === 0 && tid === 0
                                && <>
                                    <InputDocId
                                        type='number'
                                        placeholder='Document ID'
                                        onChange={(e) => {
                                            setDocId(Number(e.currentTarget?.value) || 0)
                                        }}
                                        onKeyPress={e => {
                                            if (e.key === 'Enter' && docId > 0) {
                                                dspDocInfo(upload, docId, setSignBeacons, setAutoRects)
                                                setPrep('Prepairing File')
                                            }
                                        }}
                                    />
                                    <button
                                        className="dz-browse-files"
                                        onClick={() => {
                                            if (docId > 0) {
                                                dspDocInfo(upload, docId, setSignBeacons, setAutoRects)
                                                setPrep('Prepairing File')
                                            }
                                        }}
                                    >Get New Doc</button>
                                </>
                            }
                        </div>
                </DefaultContent>
            }

            {
                currentScreen === 'error' &&
                <DefaultContent>
                    <Error text={errorText} />
                </DefaultContent>
            }

        </Wrapper>
    )
}

export default DSPSignPlacer